// src/api/index.js
import axios from 'axios'
import router from '@/router'   // adjust path if your router file is in a different place

// Use relative /api/ so it works in production behind Nginx
export const api = axios.create({
  // Prefer env var, fallback to local dev backend
  baseURL: process.env.VUE_APP_API_BASE_URL || '/api/',
  timeout: 15000
})


// ➜ Attach JWT access token on every request
api.interceptors.request.use(config => {
  const token = localStorage.getItem('access')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// ➜ Handle 401 responses: try refresh once, then logout
let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (token) {
      prom.resolve(token)
    } else {
      prom.reject(error)
    }
  })
  failedQueue = []
}

api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config

    // If no response or not 401, just reject
    if (!error.response || error.response.status !== 401) {
      return Promise.reject(error)
    }

    // Avoid infinite loop
    if (originalRequest._retry) {
      // We've already retried once, logout
      localStorage.removeItem('access')
      localStorage.removeItem('refresh')
      localStorage.removeItem('me')
      router.push('/login')
      return Promise.reject(error)
    }

    originalRequest._retry = true

    const refreshToken = localStorage.getItem('refresh')
    if (!refreshToken) {
      // No refresh token → force logout
      localStorage.removeItem('access')
      localStorage.removeItem('refresh')
      localStorage.removeItem('me')
      router.push('/login')
      return Promise.reject(error)
    }

    // If a refresh request is already in progress, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve: (token) => {
            originalRequest.headers.Authorization = 'Bearer ' + token
            resolve(api(originalRequest))
          },
          reject
        })
      })
    }

    isRefreshing = true

    try {
      // Use the same api instance so baseURL (/api/) is applied
      const res = await api.post('token/refresh/', {
        refresh: refreshToken
      })

      const newAccess = res.data.access
      localStorage.setItem('access', newAccess)

      // Update all queued requests
      processQueue(null, newAccess)

      // Retry the original request with new token
      originalRequest.headers.Authorization = 'Bearer ' + newAccess
      return api(originalRequest)
    } catch (refreshError) {
      processQueue(refreshError, null)
      // Refresh failed → logout
      localStorage.removeItem('access')
      localStorage.removeItem('refresh')
      localStorage.removeItem('me')
      router.push('/login')
      return Promise.reject(refreshError)
    } finally {
      isRefreshing = false
    }
  }
)
